import type { DynoSummary, PerformanceRecord } from '../types/buildExpansion';
import type { PublicBuildMod } from './publicBuildFilters';

/** Deliberately opt-in per URL, never persisted or inferred from another flag. */
export function isBuildStoryBeta(params: URLSearchParams): boolean {
  return params.get('beta') === '1';
}

export function selectStoryDyno(summary?: DynoSummary | null): PerformanceRecord | null {
  const candidates = [
    summary?.latest_verified,
    summary?.highest_wheel,
    summary?.highest_crank,
    ...(summary?.history ?? []),
  ];
  return (
    candidates.find((record): record is PerformanceRecord =>
      Boolean(
        record &&
        record.is_public === true &&
        record.record_type === 'dyno' &&
        Number.isFinite(record.value_primary) &&
        Number(record.value_primary) > 0
      )
    ) ?? null
  );
}

export function dynoVerificationLabel(record: PerformanceRecord): string {
  const verified = {
    shop_verified: 'Shop verified',
    event_verified: 'Event verified',
    admin_verified: 'Admin verified',
  };
  if (record.is_verified && record.verification_status in verified) {
    return verified[record.verification_status as keyof typeof verified];
  }
  return record.verification_status === 'evidence_attached'
    ? 'Evidence attached'
    : 'Owner reported';
}

/** Dates are calendar dates, not local instants; missing dates remain unknown. */
export function storyDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value.slice(0, 10) + 'T12:00:00Z');
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value.slice(0, 10))
    return null;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export interface StoryChapter {
  title: string;
  label: string;
  parts: PublicBuildMod[];
}

/** Category-led excerpts of the real record, never invented editorial claims. */
export function storyChapters(mods: PublicBuildMod[]): StoryChapter[] {
  const roots = mods.filter((m) => !m.parent_modification_id);
  const pool = roots.length ? roots : mods;
  const used = new Set<string>();
  const chapters: StoryChapter[] = [];
  const groups = [
    {
      label: 'Under the hood',
      title: 'A closer look at the build.',
      categories: /^(engine|turbo|fuel system|cooling)$/i,
    },
    {
      label: 'Behind the wheel',
      title: 'The details inside.',
      categories: /^(drivetrain|electronics|interior)$/i,
    },
  ];
  for (const group of groups) {
    const matching = pool.filter((m) => group.categories.test(m.category ?? ''));
    // Lead with one entry per system before filling from the same category.
    const systems = new Set<string>();
    const leads = matching.filter((m) => {
      const system = (m.category ?? '').toLowerCase();
      if (systems.has(system)) return false;
      systems.add(system);
      return true;
    });
    const parts = [...leads, ...matching.filter((m) => !leads.includes(m))].slice(0, 3);
    if (!parts.length) continue;
    parts.forEach((m) => used.add(String(m.id)));
    chapters.push({ label: group.label, title: group.title, parts });
  }
  if (chapters.length < 2) {
    const parts = pool.filter((m) => !used.has(String(m.id))).slice(0, 3);
    if (parts.length)
      chapters.push({
        label: 'From the parts record',
        title: 'Details that make the build.',
        parts,
      });
  }
  return chapters;
}
